import React, { useState, useEffect } from 'react';
import { Grid, Popover, TextField, IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import TagIcon from '@material-ui/icons/LocalOffer';

import Tag from './Tag';
import TagSelection from './TagSelection';


const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    textAlign: 'center',
    pointerEvents: 'auto',
    zIndex: 2,
  },
  containerHoverClear: {
    maxWidth: 'calc(100% + 4px) !important',
    overflow: 'hidden',
    '&:hover': {
      flexWrap: 'wrap',
      zIndex: 3,
    },
  },
  containerHoverBackground: {
    backgroundColor: '#fafafa',
    maxWidth: 'calc(100% + 4px) !important',
    overflow: 'hidden',
    '&:hover': {
      flexWrap: 'wrap',
      zIndex: 3,
    },
  },
  containerAlwaysExpanded: {
    flexWrap: 'wrap',
  },
  overlay: {
    position: 'absolute',
    right: '0',
    bottom: '0',
    height: '28px',
    paddingRight: '0px !important',
    paddingLeft: '60px !important',
    display: 'flex',
    background: 'linear-gradient(90deg, ' +
      'rgba(250,250,250,0) 0%, ' +
      'rgba(250,250,250,1) 100%)',
  },
  popoverRoot: {
    minWidth: '172px',
    padding: theme.spacing(0.5),
  },
  textField: {
  }
}));

export default function TagList(props) {
  const {
    materialId, tags, indeterminate, onChange, tagChoices, alwaysExpanded, onBlur, onFocus, onHover, iconOnly,
    iconClass, iconTooltip, hideOverlay,
  } = props;

  const classes = useStyles();
  classes.containerHover = hideOverlay ? classes.containerHoverClear : classes.containerHoverBackground;

  const [anchor, setAnchor] = useState(null);
  const [isHovered, setIsHovered] = useState(alwaysExpanded === true);
  const [selected, setSelected] = useState([]);
  const [selectedSecondary, setSelectedSecondary] = useState([]);
  const [textVal, setTextVal] = useState('');

  const tagSelectionOpen = Boolean(anchor);

  useEffect(() => {
    async function onTags() {
      const selectedTags = tags.filter(t => t.tagType === 'user').map(t => t.label);
      const indeterminateTags = (indeterminate ?? []).filter(t => t.tagType === 'user' && !selectedTags.includes(t.label)).map(t => t.label);
      setSelected(selectedTags);
      setSelectedSecondary(indeterminateTags);
    }
    onTags();
  }, [tags, indeterminate]);

  useEffect(() => {
    if (onHover) {
      onHover(!!tagSelectionOpen || isHovered, materialId);
    }
  }, [materialId, isHovered, onHover, tagSelectionOpen]);

  async function onTagSelection(fields) {
    setSelected(fields.tags);
    setSelectedSecondary(fields.tagsIndeterminate);
    if (onChange) {
      onChange(materialId, { selected: fields.tags, secondary: fields.tagsIndeterminate });
    }
  }

  async function onAdd(event) {
    const anchorEl = event.currentTarget.parentElement.parentElement.parentElement;
    setAnchor(anchorEl);
  }

  async function onDelete(deletedTag) {
    const newSel = selected.filter(t => t !== deletedTag.label);
    setSelected(newSel);
    const newSec = (selectedSecondary ?? []).filter(t => t !== deletedTag.label);
    setSelectedSecondary(newSec);
    if (onChange) {
      onChange(materialId, { selected: newSel, secondary: newSec });
    }
  }

  async function onTextEnter(event) {
    if (event.key == 'Enter') {
      const val = event.target.value;
      if (val.length > 0 && !tags.includes(val)) {
        var selectedTags = [];
        tags.forEach(tag => {
          if (tag.tagType == 'user') {
            selectedTags.push(tag.label);
          }
        });
        selectedTags.push(val);
        if (onChange) {
          onChange(materialId, { selected: selectedTags, secondary: selectedSecondary });
        }
      }
      setTextVal('');
    }
  }

  return (<>
    {iconOnly ? (
      <Tooltip title={iconTooltip ?? 'Set tags'}>
        <IconButton
          className={iconClass}
          onClick={(event) => { setAnchor(event.currentTarget) }}
        >
          <TagIcon />
        </IconButton>
      </Tooltip>
    ) : (
      <Grid container
        spacing={1}
        wrap='nowrap'
        className={`${classes.container} ${alwaysExpanded ? classes.containerAlwaysExpanded : classes.containerHover}`}
        onMouseEnter={() => { setIsHovered(true) }}
        onMouseLeave={() => { setIsHovered(alwaysExpanded === true) }}
      >
        {tags && tags.map((tag, i) => (
          <Grid item key={i}>
            <Tag
              label={tag.label}
              tagType={tag.tagType}
              onDelete={() => onDelete(tag)}
              showButtons={isHovered}
            />
          </Grid>
        ))}
        <Grid item>
          <Tag
            tagType='specialAdd'
            onClick={onAdd}
            showButtons={isHovered || tags.length == 0}
          />
        </Grid>
        {!hideOverlay && !isHovered && tags.length > 0 && (
          <Grid item className={classes.overlay} />
        )}
      </Grid>
    )}
    <Popover
      id='add-tags-popover'
      className={classes.popover}
      open={tagSelectionOpen}
      anchorEl={anchor}
      onClose={() => { setAnchor(null) }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <div className={classes.popoverRoot}>
        <TagSelection
          tags={tagChoices}
          selected={selected}
          indeterminate={selectedSecondary}
          setSelected={onTagSelection}
        />
        <TextField
          className={classes.textField}
          size='small'
          variant="filled"
          label="Enter new tag name"
          value={textVal}
          onChange={(e) => setTextVal(e.target.value)}
          onKeyDown={onTextEnter}
          autoFocus
          onBlur={onBlur}
          onFocus={onFocus}
        />
      </div>
    </Popover>
  </>);
}
